import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Mail, ArrowRight, Loader2, AlertCircle, KeyRound } from "lucide-react";
import Logo from "../components/Logo";
import GoldoraStrengths from "../components/GoldoraStrengths";
import { useI18n } from "../i18n";
import { loginPin, setSessionToken, requestPinReset, googleSession, fetchMe } from "../lib/api";

const STORAGE_KEY = "intelvest_user";

export default function Login() {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [resetSent, setResetSent] = useState(false);

  // Handle OAuth callback (?session_id=... or #session_id=...)
  useEffect(() => {
    const hash = window.location.hash || "";
    const sp = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : window.location.search);
    const sid = sp.get("session_id");
    if (!sid) return;
    setBusy(true);
    googleSession(sid)
      .then((d) => {
        setSessionToken(d.session_token);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(d.user)); } catch {/* */}
        // Clean the URL
        window.history.replaceState({}, "", "/");
        nav("/");
      })
      .catch(() => setErr(pt ? "Falha no login Google. Tente novamente." : "Google login failed. Try again."))
      .finally(() => setBusy(false));
  }, [nav, pt]);

  // Pre-fill email from any stored user
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const u = JSON.parse(raw);
        if (u?.email) setEmail(u.email);
      }
    } catch {/* */}
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr("");
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setErr(pt ? "Email inválido." : "Invalid email.");
      return;
    }
    if (!/^\d{6}$/.test(pin)) {
      setErr(pt ? "PIN deve ter 6 dígitos." : "PIN must be 6 digits.");
      return;
    }
    setBusy(true);
    try {
      const r = await loginPin(email, pin);
      setSessionToken(r.session_token);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(r.user)); } catch {/* */}
      nav("/");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        setErr(pt ? "Muitas tentativas. Tente em 15 minutos." : "Too many attempts. Try in 15 minutes.");
      } else if (status === 401) {
        setErr(pt ? "Email ou PIN incorretos." : "Invalid email or PIN.");
      } else {
        setErr(pt ? "Erro ao entrar. Tente novamente." : "Login error. Try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const sendReset = async () => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setErr(pt ? "Digite seu email primeiro." : "Enter your email first.");
      return;
    }
    setBusy(true);
    try {
      await requestPinReset(email);
      setResetSent(true);
    } catch {/* */}
    finally { setBusy(false); }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginGoogle = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen px-4 py-12" data-testid="page-login">
      <div className="w-full max-w-5xl mx-auto grid lg:grid-cols-5 gap-8 items-start">
        {/* Left side — selling points */}
        <div className="lg:col-span-3 order-2 lg:order-1 surface p-6 sm:p-7" data-testid="login-strengths">
          <GoldoraStrengths />
        </div>

        {/* Right side — login form */}
        <div className="lg:col-span-2 order-1 lg:order-2 w-full max-w-md mx-auto">
          <div className="text-center mb-6">
            <Logo size={48} />
            <div className="mt-3 font-brand text-[1.2rem] tracking-[0.18em] brand-metal">GOLDORA</div>
            <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.28em] text-neutral-500">
              {pt ? "Visão global. Você no controle." : "Global vision. You in control."}
            </div>
          </div>

          <div className="surface p-6 sm:p-7">
          <h1 className="font-display text-2xl text-white tracking-tight">
            {pt ? "Entrar na plataforma" : "Sign in"}
          </h1>
          <p className="mt-1 text-sm text-neutral-400">
            {pt ? "Acesso seguro com PIN de 6 dígitos." : "Secure access with 6-digit PIN."}
          </p>

          <button
            type="button"
            onClick={loginGoogle}
            data-testid="login-google-btn"
            disabled={busy}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-neutral-200 disabled:opacity-50 text-black font-medium px-4 py-2.5 rounded-md transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M22 12c0-.74-.06-1.45-.18-2.13H12v4.04h5.6c-.24 1.3-.97 2.4-2.07 3.14v2.6h3.34C20.85 17.96 22 15.21 22 12z" fill="#4285F4"/>
              <path d="M12 22c2.7 0 4.96-.9 6.61-2.43l-3.34-2.6c-.93.62-2.11.99-3.27.99-2.51 0-4.64-1.7-5.4-3.98H3.16v2.5C4.8 19.78 8.13 22 12 22z" fill="#34A853"/>
              <path d="M6.6 13.97A6.05 6.05 0 0 1 6.27 12c0-.69.12-1.36.32-1.97V7.53H3.16C2.42 8.99 2 10.45 2 12s.42 3 1.16 4.47l3.44-2.5z" fill="#FBBC05"/>
              <path d="M12 6.05c1.47 0 2.78.5 3.81 1.5l2.86-2.86C16.95 3.18 14.7 2 12 2 8.13 2 4.8 4.22 3.16 7.53l3.44 2.5C7.36 7.75 9.49 6.05 12 6.05z" fill="#EA4335"/>
            </svg>
            {pt ? "Entrar com Google" : "Continue with Google"}
          </button>

          <div className="my-5 flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-600">
            <span className="flex-1 h-px bg-white/5" />
            {pt ? "ou" : "or"}
            <span className="flex-1 h-px bg-white/5" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            <Field icon={Mail} label={pt ? "Email" : "Email"}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                inputMode="email"
                data-testid="login-email"
                placeholder="seu@email.com"
                className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2.5 focus:outline-none focus:border-gold-500 text-white"
              />
            </Field>

            <Field icon={Lock} label={pt ? "PIN (6 dígitos)" : "PIN (6 digits)"}>
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                autoComplete="current-password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                data-testid="login-pin"
                placeholder="••••••"
                className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2.5 focus:outline-none focus:border-gold-500 text-white tracking-[0.6em] text-center font-mono text-lg"
              />
            </Field>

            {err && (
              <div className="flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2" data-testid="login-error">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>{err}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy || pin.length < 6 || !email}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-medium px-4 py-2.5 rounded-md transition-colors"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              {pt ? "Entrar" : "Sign in"}
            </button>
          </form>

          <div className="mt-5 pt-5 border-t border-white/5 flex flex-col gap-2 text-sm text-center">
            {resetSent ? (
              <p className="text-emerald-300 text-xs">
                {pt ? "Se essa conta existe, você receberá um email em instantes." : "If that account exists, an email is on its way."}
              </p>
            ) : (
              <button
                type="button"
                onClick={sendReset}
                data-testid="login-forgot"
                className="text-gold-300 hover:text-gold-200 text-sm inline-flex items-center justify-center gap-1.5"
              >
                <KeyRound size={13} /> {pt ? "Esqueci meu PIN" : "Forgot PIN"}
              </button>
            )}
            <Link
              to="/cadastro"
              data-testid="link-register"
              className="text-neutral-400 hover:text-white text-sm"
            >
              {pt ? "Não tem conta? " : "No account? "}
              <span className="text-gold-300">{pt ? "Criar agora" : "Create now"}</span>
            </Link>
          </div>
        </div>

        <div className="mt-5 text-center text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-700">
          {pt ? "Conteúdo educacional · Sem aconselhamento financeiro" : "Educational content · No financial advice"}
        </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon size={11} />}
        {label}
      </label>
      {children}
    </div>
  );
}
